import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getCustomerDashboard } from '../api/dashboard';
import { getTickets } from '../api/tickets';
import { toast } from 'sonner';
import { Plus, Search, Filter, Calendar, Star, MoreVertical, X, Tag as TagIcon } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../components/ui/button';
import Input from '../components/ui/input';
import DashboardLayout from '../components/DashboardLayout';
import api from '../api/axios';

const CustomerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dashboardData, setDashboardData] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState({
    status: '',
    priority: '',
    category_id: '',
    dateFrom: '',
    dateTo: '',
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeFilter, setActiveFilter] = useState(() => {
    return searchParams.get('filter') || 'all';
  });
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);

  useEffect(() => {
    fetchDashboardData();
    fetchCategories();
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, searchTerm, advancedFilters]);

  useEffect(() => {
    const filterFromUrl = searchParams.get('filter') || 'all';
    const fromFromUrl = searchParams.get('from') || '';
    const toFromUrl = searchParams.get('to') || '';
    
    if (filterFromUrl !== activeFilter) {
      setActiveFilter(filterFromUrl);
    }
    
    if (fromFromUrl !== advancedFilters.dateFrom || toFromUrl !== advancedFilters.dateTo) {
      setAdvancedFilters(prev => ({
        ...prev,
        dateFrom: fromFromUrl,
        dateTo: toFromUrl
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchDashboardData = async () => {
    try {
      const response = await getCustomerDashboard();
      if (response.success) {
        setDashboardData(response.data);
      }
    } catch (error) {
      toast.error('Failed to load dashboard data');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      // Clear any previous selection when loading new tickets
      setSelectedTicketIds([]);
      const params = { ...advancedFilters };
      
      // Apply active filter (only if not already set in advancedFilters)
      if (!params.status) {
        switch (activeFilter) {
          case 'open':
            params.status = 'open';
            break;
          case 'resolved':
            params.status = 'resolved';
            break;
          case 'closed':
            params.status = 'closed';
            break;
          case 'favorites':
            // Favorites will be filtered client-side from localStorage
            break;
          case 'recent':
            // Recent will be filtered client-side from localStorage
            break;
          case 'all':
          default:
            // No additional filters (backend already returns only this customer's tickets)
            break;
        }
      }
      
      // Apply date range filter
      if (params.dateFrom) {
        params.date_from = params.dateFrom;
        delete params.dateFrom;
      }
      if (params.dateTo) {
        params.date_to = params.dateTo;
        delete params.dateTo;
      }
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === false) delete params[key];
      });

      const response = await getTickets(params);
      if (response.success) {
        let filteredTickets = response.data.tickets;
        
        // Load favorites from localStorage for this user
        const favoriteKey = `favorite_tickets_${user?.id}`;
        const storedFavoriteTickets = JSON.parse(localStorage.getItem(favoriteKey) || '[]');
        setFavoriteIds(storedFavoriteTickets);

        // Apply favorites filter (client-side)
        if (activeFilter === 'favorites') {
          filteredTickets = filteredTickets.filter(t => storedFavoriteTickets.includes(t.id));
        }
        
        // Apply recent filter (client-side)
        if (activeFilter === 'recent') {
          const recentKey = `recent_tickets_${user?.id}`;
          const recentTickets = JSON.parse(localStorage.getItem(recentKey) || '[]');
          const recentIds = recentTickets.map(t => t.ticketId);
          filteredTickets = filteredTickets.filter(t => recentIds.includes(t.id));
        }
        
        if (searchTerm) {
          filteredTickets = filteredTickets.filter(
            (ticket) =>
              ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
              ticket.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
              ticket.ticket_number.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }
        
        setTickets(filteredTickets);
      }
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, searchTerm, advancedFilters]);

  // Update URL when filter changes
  // eslint-disable-next-line no-unused-vars
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    if (filter === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ filter });
    }
  };

  // --- Helper Components for the Table ---
  const PriorityBadge = ({ priority }) => {
    const styles = {
      urgent: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20 shadow-red-500/10',
      high: 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/20 shadow-orange-500/10',
      medium: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20 shadow-yellow-500/10',
      low: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20 shadow-blue-500/10',
    };
    const defaultStyle = styles.low;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border shadow-sm ${styles[priority?.toLowerCase()] || defaultStyle}`}>
        {priority?.toUpperCase() || 'LOW'}
      </span>
    );
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      new: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border-indigo-500/20',
      open: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20',
      in_progress: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20',
      resolved: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20',
      closed: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-slate-700',
      cancelled: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20',
    };
    const defaultStyle = styles.open;

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status?.toLowerCase()] || defaultStyle}`}>
        <span className="relative flex h-1.5 w-1.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-current"></span>
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-current"></span>
        </span>
        {status?.replace('_', ' ').toUpperCase() || 'OPEN'}
      </span>
    );
  };

  // Favorite helpers
  const isFavorite = (id) => favoriteIds.includes(id);

  const saveFavoriteIds = (ids) => {
    const favoriteKey = `favorite_tickets_${user?.id}`;
    localStorage.setItem(favoriteKey, JSON.stringify(ids));
    setFavoriteIds(ids);
  };

  const toggleFavorite = (ticketId) => {
    const newFavorites = favoriteIds.includes(ticketId)
      ? favoriteIds.filter((id) => id !== ticketId)
      : [...favoriteIds, ticketId];
    saveFavoriteIds(newFavorites);
    if (activeFilter === 'favorites') {
      fetchTickets();
    }
  };

  // Selection helpers
  const isTicketSelected = (id) => selectedTicketIds.includes(id);
  const areAllSelected = tickets.length > 0 && selectedTicketIds.length === tickets.length;

  const toggleSelectAll = () => {
    if (areAllSelected) {
      setSelectedTicketIds([]);
    } else {
      setSelectedTicketIds(tickets.map((t) => t.id));
    }
  };

  const toggleTicketSelection = (id) => {
    setSelectedTicketIds((prev) =>
      prev.includes(id) ? prev.filter((tid) => tid !== id) : [...prev, id]
    );
  };

  const clearSelection = () => setSelectedTicketIds([]);

  const addSelectedToFavorites = () => {
    if (selectedTicketIds.length === 0) return;
    const combined = Array.from(new Set([...favoriteIds, ...selectedTicketIds]));
    saveFavoriteIds(combined);
    toast.success(`Added ${selectedTicketIds.length} ticket(s) to favorites`);
    clearSelection();
    if (activeFilter === 'favorites') {
      fetchTickets();
    }
  };

  const removeSelectedFromFavorites = () => {
    if (selectedTicketIds.length === 0) return;
    const remaining = favoriteIds.filter((id) => !selectedTicketIds.includes(id));
    saveFavoriteIds(remaining);
    toast.success(`Removed ${selectedTicketIds.length} ticket(s) from favorites`);
    clearSelection();
    if (activeFilter === 'favorites') {
      fetchTickets();
    }
  };

  return (
    <DashboardLayout userRole={user?.role || 'customer'}>
      <div className="soft-mesh-gradient min-h-full">
          {/* Header – compact on mobile, same as admin */}
          <div className="border-b border-gray-200 dark:border-gray-700 glass-card px-3 md:px-6 py-3 md:py-4 relative z-10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h1 className="text-lg md:text-2xl font-semibold text-gray-900 dark:text-gray-100">Tickets</h1>
                <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {dashboardData?.total ?? tickets.length} {(dashboardData?.total ?? tickets.length) === 1 ? 'Ticket' : 'Tickets'}
                </p>
              </div>
              <Button
                variant="unstyled"
                onClick={() => navigate('/tickets/create')}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap min-h-[44px] h-11 px-4 md:px-6 text-sm font-semibold bg-gradient-to-r from-indigo-600 via-blue-500 to-cyan-500 text-white rounded-xl shadow-xl shadow-indigo-500/40 hover:shadow-indigo-500/60 hover:-translate-y-1 hover:scale-[1.05] active:scale-95 transition-all duration-300 touch-manipulation"
              >
                <Plus className="w-4 h-4 shrink-0" />
                <span className="hidden xs:inline leading-none">Create</span>
                <span className="xs:hidden leading-none">New</span>
              </Button>
            </div>
          </div>

          {/* Search and Quick Filters – mobile-friendly, same as admin */}
          <div className="mt-3 md:mt-4 mx-3 md:mx-0 border border-gray-200 dark:border-gray-700 rounded-xl md:rounded-none md:border-x-0 md:border-t-0 bg-white/80 dark:bg-slate-800/80 md:glass-card px-3 md:px-6 py-3 md:py-4 relative z-10">
            <div className="flex flex-col gap-3 md:gap-4">
              <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-3">
                <div className="flex-1 relative min-w-0 w-full">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400 dark:text-gray-500 pointer-events-none" />
                  <Input
                    type="text"
                    placeholder="Search tickets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full min-w-0 pl-9 md:pl-10 pr-3 py-2.5 md:py-2.5 h-11 md:min-h-[44px] bg-white dark:bg-slate-700 border border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 rounded-xl md:rounded-lg transition-all duration-300 shadow-sm text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-400"
                  />
                </div>

                <div className="flex items-center gap-2 md:gap-3 overflow-x-auto pb-1 md:pb-0 -mx-1 px-1 md:mx-0 md:px-0 scrollbar-thin">
                  <div className="relative flex-shrink-0">
                    <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-600 dark:text-blue-400 pointer-events-none z-10" />
                    <select
                      value={advancedFilters.status}
                      onChange={(e) => setAdvancedFilters({ ...advancedFilters, status: e.target.value })}
                      className="text-sm pl-9 pr-3 py-2.5 h-11 md:min-h-[44px] border border-gray-300 dark:border-gray-600 rounded-xl md:rounded-lg bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none shadow-sm transition-all touch-manipulation min-w-[120px]"
                    >
                      <option value="">All Status</option>
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="pending">Pending</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>

                  <div className="relative flex-shrink-0">
                    <TagIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-600 dark:text-red-400 pointer-events-none z-10" />
                    <select
                      value={advancedFilters.priority}
                      onChange={(e) => setAdvancedFilters({ ...advancedFilters, priority: e.target.value })}
                      className="text-sm pl-9 pr-3 py-2.5 h-11 md:min-h-[44px] border border-gray-300 dark:border-gray-600 rounded-xl md:rounded-lg bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none shadow-sm transition-all touch-manipulation min-w-[120px]"
                    >
                      <option value="">All Priority</option>
                      <option value="urgent">Urgent</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>

                  <button
                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                    className={`flex-shrink-0 px-3 md:px-4 py-2.5 h-11 md:min-h-[44px] text-sm border border-gray-300 dark:border-gray-600 rounded-xl md:rounded-lg flex items-center gap-2 font-medium transition-all shadow-sm touch-manipulation ${
                      showAdvancedFilters 
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-400 text-blue-700 dark:text-blue-300' 
                        : 'bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    <Filter className={`w-4 h-4 ${showAdvancedFilters ? 'text-blue-600' : 'text-gray-500'}`} />
                    <span>More</span>
                    {(advancedFilters.status || advancedFilters.priority || advancedFilters.category_id || advancedFilters.dateFrom || advancedFilters.dateTo) && (
                      <span className="ml-1 px-2 py-0.5 bg-blue-600 text-white text-xs font-bold rounded-full min-w-[20px] text-center">
                        {[
                          advancedFilters.status,
                          advancedFilters.priority,
                          advancedFilters.category_id,
                          advancedFilters.dateFrom,
                          advancedFilters.dateTo
                        ].filter(Boolean).length}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            {/* Advanced Filters Panel */}
            {showAdvancedFilters && (
              <div className="mt-4 p-3 md:p-5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-xl shadow-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <Filter className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    Advanced Filters
                  </h4>
                  <button
                    onClick={() => {
                      setAdvancedFilters({
                        status: '',
                        priority: '',
                        category_id: '',
                        dateFrom: '',
                        dateTo: '',
                      });
                      setShowAdvancedFilters(false);
                    }}
                    className="px-3 py-2 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 flex items-center gap-2 transition-all text-gray-700 dark:text-gray-200 touch-manipulation self-start sm:self-auto"
                    style={{ minHeight: '44px' }}
                  >
                    <X className="w-3.5 h-3.5" />
                    Clear All
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                  {/* Status */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                      Status
                    </label>
                    <div className="relative">
                      <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-600 dark:text-blue-400 pointer-events-none z-10" />
                      <select
                        value={advancedFilters.status}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, status: e.target.value })}
                        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-gray-700 dark:text-gray-300 hover:border-blue-400 dark:hover:border-blue-500 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 cursor-pointer transition-all appearance-none shadow-sm"
                      >
                        <option value="">All Statuses</option>
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                        <option value="pending">Pending</option>
                      </select>
                    </div>
                  </div>

                  {/* Priority */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                      Priority
                    </label>
                    <div className="relative">
                      <TagIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-600 dark:text-red-400 pointer-events-none z-10" />
                      <select
                        value={advancedFilters.priority}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, priority: e.target.value })}
                        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-gray-700 dark:text-gray-300 hover:border-blue-400 dark:hover:border-blue-500 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 cursor-pointer transition-all appearance-none shadow-sm"
                      >
                        <option value="">All Priorities</option>
                        <option value="urgent">Urgent</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>
                  </div>

                  {/* Category */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                      Category
                    </label>
                    <div className="relative">
                      <TagIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-purple-600 dark:text-purple-400 pointer-events-none z-10" />
                      <select
                        value={advancedFilters.category_id}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, category_id: e.target.value })}
                        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-gray-700 dark:text-gray-300 hover:border-blue-400 dark:hover:border-blue-500 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 cursor-pointer transition-all appearance-none shadow-sm"
                      >
                        <option value="">All Categories</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Date From */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                      Date From
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-indigo-600 dark:text-indigo-400 pointer-events-none z-10" />
                      <input
                        type="date"
                        value={advancedFilters.dateFrom}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, dateFrom: e.target.value })}
                        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-gray-700 dark:text-gray-300 hover:border-blue-400 dark:hover:border-blue-500 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 transition-all shadow-sm"
                      />
                    </div>
                  </div>

                  {/* Date To */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                      Date To
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-indigo-600 dark:text-indigo-400 pointer-events-none z-10" />
                      <input
                        type="date"
                        value={advancedFilters.dateTo}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, dateTo: e.target.value })}
                        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-gray-700 dark:text-gray-300 hover:border-blue-400 dark:hover:border-blue-500 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 transition-all shadow-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Table + Mobile cards – same layout as admin */}
          <div className="px-3 md:px-6 py-3 md:py-4 relative z-10">
            {selectedTicketIds.length > 0 && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-0 md:px-0 py-3 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800 text-sm text-blue-900 dark:text-blue-200 transition-colors duration-300">
                <span className="font-medium flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-200 dark:bg-blue-800 text-blue-700 dark:text-blue-100 text-xs font-bold">
                    {selectedTicketIds.length}
                  </span>
                  <span>selected</span>
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={addSelectedToFavorites}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap h-11 text-xs px-3 md:px-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 text-white rounded-lg tracking-wide shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 hover:-translate-y-0.5 hover:scale-[1.04] active:scale-95 transition-all duration-300 font-semibold"
                  >
                    <Star className="w-3.5 h-3.5 shrink-0" />
                    <span className="leading-none">Add to favorites</span>
                  </button>
                  <button
                    type="button"
                    onClick={removeSelectedFromFavorites}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap h-11 text-xs px-3 md:px-4 bg-gradient-to-r from-amber-500 via-orange-500 to-yellow-400 text-white rounded-lg tracking-wide shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 hover:-translate-y-0.5 hover:scale-[1.04] active:scale-95 transition-all duration-300 font-semibold"
                  >
                    <Star className="w-3.5 h-3.5 shrink-0" />
                    <span className="leading-none">Remove from favorites</span>
                  </button>
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap h-11 text-xs px-3 md:px-4 rounded-lg tracking-wide bg-gradient-to-r from-slate-100 via-slate-50 to-violet-100 text-slate-800 hover:text-slate-900 shadow-md shadow-violet-300/40 hover:shadow-violet-400/60 hover:-translate-y-0.5 hover:scale-[1.04] active:scale-95 transition-all duration-300 dark:from-slate-700 dark:via-slate-800 dark:to-violet-700 dark:text-slate-100 dark:hover:text-white font-semibold"
                  >
                    <span className="leading-none">Clear</span>
                  </button>
                </div>
              </div>
            )}

            {/* Mobile: card list (same as admin tickets) */}
            <div className="md:hidden space-y-3 pb-4">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
                </div>
              ) : tickets.length === 0 ? (
                <div className="text-center py-12 px-4 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400">No tickets found. Create your first ticket!</p>
                </div>
              ) : (
                tickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                    className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-3.5 shadow-sm active:scale-[0.99] transition-transform cursor-pointer"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 pt-0.5" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500/30"
                          checked={isTicketSelected(ticket.id)}
                          onChange={() => toggleTicketSelection(ticket.id)}
                          aria-label={`Select ticket ${ticket.ticket_number}`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs font-medium text-indigo-600 dark:text-indigo-400">{ticket.ticket_number}</span>
                          <StatusBadge status={ticket.status} />
                          <PriorityBadge priority={ticket.priority} />
                        </div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white mt-1 line-clamp-2">{ticket.subject || 'No subject'}</p>
                        <div className="flex items-center justify-between mt-2 flex-wrap gap-1">
                          <span className="text-xs text-gray-500 dark:text-gray-400">{ticket.category_name || 'General'}</span>
                          <span className="text-xs text-gray-600 dark:text-gray-300 truncate max-w-[120px]">
                            {ticket.assigned_to_name ? (ticket.assigned_to_name || '').replace(/0+$/, '').trim() : 'Unassigned'}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-shrink-0" onClick={e => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleFavorite(ticket.id); }}
                          className="text-gray-400 hover:text-yellow-500 p-1"
                          aria-label={isFavorite(ticket.id) ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <Star className={`w-4 h-4 ${isFavorite(ticket.id) ? 'fill-yellow-500 text-yellow-500' : ''}`} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block overflow-hidden rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl shadow-gray-200/50 dark:shadow-black/40">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50/50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-slate-700 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      <th className="p-4 w-[50px] font-medium text-center">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500/30"
                          checked={areAllSelected}
                          onChange={toggleSelectAll}
                          aria-label="Select all tickets"
                        />
                      </th>
                      <th className="p-4 w-[100px] font-semibold">Number</th>
                      <th className="p-4 w-[120px] font-semibold">Opened</th>
                      <th className="p-4 w-auto min-w-[200px] font-semibold">Description</th>
                      <th className="p-4 w-[110px] font-semibold">Priority</th>
                      <th className="p-4 w-[130px] font-semibold">State</th>
                      <th className="p-4 w-[140px] font-semibold">Category</th>
                      <th className="p-4 w-[160px] font-semibold">Assigned To</th>
                      <th className="p-4 w-[120px] font-semibold hidden xl:table-cell">Updated</th>
                      <th className="p-4 w-[50px]"></th>
                      <th className="p-4 w-[50px]"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                    {loading ? (
                      <tr>
                        <td colSpan="11" className="p-8 text-center">
                          <div className="animate-spin w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
                        </td>
                      </tr>
                    ) : tickets.length === 0 ? (
                      <tr>
                        <td colSpan="11" className="p-8 text-center text-gray-500 dark:text-gray-400">
                          No tickets found. Create your first ticket!
                        </td>
                      </tr>
                    ) : (
                      tickets.map((ticket) => (
                        <tr 
                          key={ticket.id}
                          onClick={() => navigate(`/tickets/${ticket.id}`)}
                          className="group bg-white dark:bg-slate-900 hover:bg-indigo-50/30 dark:hover:bg-indigo-900/10 transition-all duration-200 cursor-pointer hover:shadow-lg hover:shadow-indigo-500/5 hover:-translate-y-[1px]"
                        >
                          {/* Checkbox */}
                          <td className="p-4 text-center" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500/30"
                              checked={isTicketSelected(ticket.id)}
                              onChange={() => toggleTicketSelection(ticket.id)}
                              aria-label={`Select ticket ${ticket.ticket_number}`}
                            />
                          </td>

                          {/* Number */}
                          <td className="p-4 font-mono text-xs font-medium text-indigo-600 dark:text-indigo-400">
                            {ticket.ticket_number || 'N/A'}
                          </td>

                          {/* Date */}
                          <td className="p-4 text-xs text-gray-500 dark:text-gray-400">
                            <div className="font-medium text-gray-700 dark:text-gray-300">
                              {new Date(ticket.created_at).toLocaleDateString(undefined, { month:'short', day:'numeric' })}
                            </div>
                            <div>{new Date(ticket.created_at).toLocaleTimeString(undefined, { hour:'2-digit', minute:'2-digit' })}</div>
                          </td>

                          {/* Description */}
                          <td className="p-4">
                            <div className="font-medium text-sm text-gray-900 dark:text-white line-clamp-1 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors" title={ticket.subject}>
                              {ticket.subject || 'No subject'}
                            </div>
                          </td>

                          {/* Priority Badge */}
                          <td className="p-4">
                            <PriorityBadge priority={ticket.priority} />
                          </td>

                          {/* Status Badge */}
                          <td className="p-4">
                            <StatusBadge status={ticket.status} />
                          </td>

                          {/* Category */}
                          <td className="p-4 text-xs text-gray-500 dark:text-gray-400">
                            <span className="px-2 py-1 rounded bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-300">
                              {ticket.category_name || 'General'}
                            </span>
                          </td>

                          {/* Assigned To */}
                          <td className="p-4">
                            {ticket.assigned_to_name ? (
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-[10px] text-white font-bold shadow-sm">
                                  {(ticket.assigned_to_name || '').replace(/0+$/, '').trim().charAt(0).toUpperCase()}
                                </div>
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate max-w-[100px]">
                                  {(ticket.assigned_to_name || '').replace(/0+$/, '').trim()}
                                </span>
                              </div>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 text-[10px] font-medium text-gray-500 dark:text-gray-400 bg-gray-100/50 dark:bg-slate-700/50 border border-gray-200/50 dark:border-slate-600/50 rounded-md">
                                Unassigned
                              </span>
                            )}
                          </td>

                          {/* Updated */}
                          <td className="p-4 text-xs text-gray-400 dark:text-gray-500 hidden xl:table-cell">
                            {new Date(ticket.updated_at).toLocaleDateString()}
                          </td>

                          {/* Actions: Favorite */}
                          <td className="p-4 text-center">
                            <button 
                              onClick={(e) => { e.stopPropagation(); toggleFavorite(ticket.id); }}
                              className="text-gray-400 hover:text-yellow-500 hover:scale-110 transition-transform"
                              title={isFavorite(ticket.id) ? 'Remove from favorites' : 'Add to favorites'}
                              aria-label={isFavorite(ticket.id) ? 'Remove from favorites' : 'Add to favorites'}
                            >
                              <Star className={`w-4 h-4 ${isFavorite(ticket.id) ? 'fill-yellow-500 text-yellow-500' : ''}`} />
                            </button>
                          </td>

                          {/* Actions: Menu */}
                          <td className="p-4 text-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // Show menu (placeholder for future functionality)
                              }}
                              className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                              title="More options"
                              aria-label="More options"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
      </div>
    </DashboardLayout>
  );
};

export default CustomerDashboard;
